import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Pan and zoom for the resort plan.
 *
 * The requirement asks for pinch-zoom, double-tap zoom and a reset control.
 *
 * **Amended 31 Aug: the home view now FITS rather than FILLS.** The original
 * reasoning was sound for the plan it was written against — a 2:1 landscape
 * drawing fitted into a portrait phone leaves a 200 px strip floating in a
 * 480 px box, so filling the viewport wasted less screen. That plan is gone.
 * The real one is 389 m by 512 m, i.e. PORTRAIT, so a phone's viewport and the
 * plan are now nearly the same shape and "fill" simply over-zooms — it handed
 * every guest a map already cropped past its own edges.
 *
 * Worse, `MIN_SCALE` was 1, and scale 1 means "the plan's WIDTH fits the box".
 * On a tall plan that is not the whole plan, so there was no zoom level at
 * which the resort was fully visible — on a desktop window it was about three
 * times too big with no way out. Scale 1 keeps its meaning; the floor is now
 * whatever scale contains the whole plan, which is below 1 on any wide box.
 *
 * Deliberately hand-rolled rather than a library. It is one transform on one
 * element; a gesture library would be larger than this file and would still need
 * the clamping, which is the only part with any judgement in it.
 */

export interface View {
  /** Scale, 1 = whole resort visible. */
  s: number;
  /** Translation in CSS pixels, applied after the scale. */
  x: number;
  y: number;
}

export const FIT: View = { s: 1, x: 0, y: 0 };

/** Never smaller than this, whatever the box: below it the resort is a stamp. */
const ABS_MIN_SCALE = 0.3;
const MAX_SCALE = 5;
/** The plan's own aspect. The stage is width-locked, so height follows.
 *  Must match `.map-stage`'s aspect-ratio in app.css and VIEW in
 *  data/resort-plan.ts, which is generated from the real site: 405 m along the
 *  beach by 512 m of beach-plus-resort-plus-verge. Note it is now greater than
 *  1 — the site is deeper than it is wide, which the old hand-drawn plan had
 *  backwards. */
const PLAN_ASPECT = 517 / 353;
/** A drag this small is a tap. Without it, every pin press pans a few pixels
 *  and the click never lands. */
export const TAP_SLOP = 6;

interface Point { x: number; y: number }

/**
 * The scale at which the entire plan is visible.
 *
 * Scale 1 fits the plan's *width*. The plan is taller than it is wide, so on
 * anything but a very tall narrow box the whole drawing needs a scale below 1 —
 * which is exactly what the old `MIN_SCALE = 1` made unreachable. Capped at 1
 * so a tall phone does not blow the plan up past its natural width to fill the
 * height.
 */
function containScale(wrap: DOMRect): number {
  const stageH = wrap.width * PLAN_ASPECT;
  if (stageH <= 0) return 1;
  return Math.min(1, wrap.height / stageH);
}

/** The zoom floor for this box. Dynamic, because "the whole plan" is a
 *  different scale on a phone and on a desktop window. */
function minScale(wrap: DOMRect): number {
  return Math.max(ABS_MIN_SCALE, containScale(wrap));
}

/** Keep the plan from being flung off screen: never pan past its own edges. */
function clamp(view: View, wrap: DOMRect): View {
  const stageW = wrap.width;
  const stageH = wrap.width * PLAN_ASPECT;
  const maxX = Math.max(0, (stageW * view.s - wrap.width) / 2);
  const maxY = Math.max(0, (stageH * view.s - wrap.height) / 2);
  return {
    s: view.s,
    x: Math.min(maxX, Math.max(-maxX, view.x)),
    y: Math.min(maxY, Math.max(-maxY, view.y)),
  };
}

/** Scale about a fixed point, so the thing under the fingers stays put. */
function scaleAbout(view: View, nextScale: number, at: Point, wrap: DOMRect): View {
  const s = Math.min(MAX_SCALE, Math.max(minScale(wrap), nextScale));
  const cx = at.x - wrap.left - wrap.width / 2;
  const cy = at.y - wrap.top - wrap.height / 2;
  const k = s / view.s;
  return clamp({ s, x: cx - (cx - view.x) * k, y: cy - (cy - view.y) * k }, wrap);
}

/** The whole resort, centred. What someone opening the map expects to see. */
function homeView(wrap: DOMRect): View {
  return { s: minScale(wrap), x: 0, y: 0 };
}

export function usePanZoom(wrapRef: RefObject<HTMLElement | null>) {
  const [view, setView] = useState<View>(FIT);
  const [panning, setPanning] = useState(false);
  /** Wrap width in CSS pixels. The plan's on-screen width is this times the
   *  scale, which is the only honest measure of how crowded the labels are. */
  const [wrapWidth, setWrapWidth] = useState(0);
  /** Set while a drag is in progress, so a pin's click handler can bail. */
  const dragged = useRef(false);

  const pointers = useRef(new Map<number, Point>());
  /** Where the pointer went down. The drag threshold has to be measured from
   *  here, not from the previous move event — see `onPointerMove`. */
  const origin = useRef<Point | null>(null);
  const pinch = useRef<{ dist: number; scale: number } | null>(null);
  const lastTap = useRef(0);

  const rect = () => wrapRef.current?.getBoundingClientRect() ?? null;

  /** Centre a percentage point of the plan. Omit the scale to keep the home
   *  zoom, which is what opening the map on the next event should do — move to
   *  it, not dive into it. */
  const focusOn = useCallback((xPct: number, yPct: number, scale?: number) => {
    const r = rect();
    if (!r) return;
    const s = scale ?? homeView(r).s;
    const stageH = r.width * PLAN_ASPECT;
    const dx = (xPct / 100 - 0.5) * r.width;
    const dy = (yPct / 100 - 0.5) * stageH;
    setView(clamp({ s, x: -s * dx, y: -s * dy }, r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => {
    const r = rect();
    setView(r ? homeView(r) : FIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The home view depends on the box, so it has to be measured, and re-measured
  // when the phone is rotated or the desktop window dragged.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const sync = () => {
      const r = el.getBoundingClientRect();
      setWrapWidth(r.width);
      setView((v) => (v === FIT ? homeView(r) : clamp(v, r)));
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [wrapRef]);

  const zoomBy = useCallback((factor: number) => {
    const r = rect();
    if (!r) return;
    setView((v) => scaleAbout(v, v.s * factor, { x: r.left + r.width / 2, y: r.top + r.height / 2 }, r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wheel has to be a native non-passive listener — React's synthetic one
  // cannot preventDefault, and without that the page scrolls under the map.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      setView((v) => scaleAbout(v, v.s * Math.exp(-e.deltaY / 320), { x: e.clientX, y: e.clientY }, r));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [wrapRef]);

  const onPointerDown = (e: React.PointerEvent) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) origin.current = { x: e.clientX, y: e.clientY };
    dragged.current = false;
    if (pointers.current.size === 1) {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      setPanning(true);
    }
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: view.s };
    }
  };

  /**
   * Both halves of this used to compare against the PREVIOUS move event, and
   * both were wrong for the same reason: a per-event delta is not displacement.
   *
   * It panned on any movement at all, with no threshold. A mouse jitters a
   * pixel or two between press and release, so every click nudged the plan —
   * measured, three clicks at one coordinate walked it 18 px — and whatever you
   * were aiming at slid out from under the cursor. That is what "clicking isn't
   * working" looks like from the outside: the tap registers fine, at a spot the
   * map has just moved away from.
   *
   * And `dragged` only tripped when a SINGLE event jumped more than the slop,
   * so a slow drag across the whole plan never counted as a drag: it ended as a
   * tap, opening a venue sheet or dropping a pick.
   *
   * Both are fixed by measuring from where the pointer went down.
   */
  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const next = { x: e.clientX, y: e.clientY };
    const r = rect();
    if (!r) return;

    if (pointers.current.size >= 2 && pinch.current) {
      pointers.current.set(e.pointerId, next);
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      dragged.current = true;
      setView((v) => scaleAbout(v, pinch.current!.scale * (dist / pinch.current!.dist), mid, r));
      return;
    }

    const start = origin.current;
    if (start && !dragged.current) {
      if (Math.hypot(next.x - start.x, next.y - start.y) <= TAP_SLOP) {
        // Still a tap. Deliberately does NOT record `next`, so when the drag
        // does start the first delta is measured from the press and the plan
        // picks up exactly where the finger is rather than jumping.
        return;
      }
      dragged.current = true;
    }

    pointers.current.set(e.pointerId, next);
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    setView((v) => clamp({ s: v.s, x: v.x + dx, y: v.y + dy }, r));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) {
      origin.current = null;
      setPanning(false);
      // Double tap zooms in on the spot tapped, and out again once zoomed.
      if (!dragged.current) {
        const now = Date.now();
        if (now - lastTap.current < 300) {
          const r = rect();
          if (r) {
            const home = homeView(r);
            setView((v) => (v.s > home.s + 0.15 ? home : scaleAbout(v, v.s * 2.2, { x: e.clientX, y: e.clientY }, r)));
          }
          lastTap.current = 0;
        } else {
          lastTap.current = now;
        }
      }
    }
  };

  return {
    view,
    panning,
    /** How wide the whole plan currently draws, in CSS pixels. */
    planWidth: wrapWidth * view.s,
    /** True if the gesture that just ended was a drag, not a tap. */
    wasDragged: () => dragged.current,
    focusOn,
    reset,
    zoomBy,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
}
